import apiClient from './apiClient';
import { t } from '../i18n';
import { syncSignedInHint } from '../utils/signedInHint';

// Token storage keys
const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_INFO_KEY = 'user_info';

// 后端认证接口返回的英文错误文案作为 i18n key 翻译；未收录的原样显示。
const mapAuthError = (msg) => (msg ? t(msg) : msg);

// Google / GitHub：发起前生成随机 nonce 存 sessionStorage（只有本标签页有），
// 换码 / 完成绑定时带回，API 校验一致才放行（防登录/绑定 CSRF）。
export const OAUTH_LOGIN_NONCE_KEY = 'yc_oauth_login_nonce';
export const OAUTH_BIND_NONCE_KEY = 'yc_oauth_bind_nonce';

export const newOAuthNonce = (key) => {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    const nonce = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    try { sessionStorage.setItem(key, nonce); } catch (_) { /* storage blocked: exchange will fail closed */ }
    return nonce;
};

/** Read once and forget. */
export const takeOAuthNonce = (key) => {
    try {
        const v = sessionStorage.getItem(key) || '';
        sessionStorage.removeItem(key);
        return v;
    } catch (_) {
        return '';
    }
};

export const authService = {
    // 登录
    login: async (username, password, cfkey, remember = false) => {
        try {
            const response = await apiClient.post('/auth/login', {
                username,
                password,
                cfkey, // 兼容字段（当前验证码由 RateLimitMiddleware + captcha.la 弹窗处理）
                remember
            }, {
                // Auth pages do their own messaging — skip the global error toast
                // so a wrong password doesn't double-toast.
                skipGlobalErrorToast: true,
            });

            // 处理响应，支持新的JWT响应格式
            const data = response.data;

            if (data.success) {
                // 账号开启了两步验证：后端此时不发 token，只发一次性挑战令牌，
                // 由前端引导用户输入验证码后调用 verify2fa 完成登录。
                if (data.requires_2fa) {
                    return {
                        success: true,
                        requires2fa: true,
                        challengeToken: data.challenge_token
                    };
                }

                // 保存令牌 - 支持新的JWT格式
                if (data.token) {
                    localStorage.setItem(ACCESS_TOKEN_KEY, data.token);
                }

                if (data.refresh_token) {
                    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
                }

                // 保存用户信息
                if (data.user) {
                    localStorage.setItem(USER_INFO_KEY, JSON.stringify(data.user));
            syncSignedInHint();
                }

                // 构建返回值格式以兼容现有代码
                return {
                    success: true,
                    data: {
                        accessToken: data.token,
                        refreshToken: data.refresh_token,
                        user: data.user
                    }
                };
            }

            return {
                success: false,
                message: mapAuthError(data.error) || t('Sign-in failed'),
                // pending 且邮箱未验证时后端会带上 resendable + email，
                // 前端据此展示"重发验证邮件"入口。
                resendable: !!data.resendable,
                email: data.email
            };
        } catch (error) {
            const errData = error.response?.data || {};
            return {
                success: false,
                message: mapAuthError(errData.error) || error.message || t('Sign-in request failed'),
                resendable: !!errData.resendable,
                email: errData.email
            };
        }
    },

    // 完成两步验证登录（code 可为 6 位验证码或 10 位备用码）
    verify2fa: async (challengeToken, code, remember = false) => {
        try {
            const response = await apiClient.post('/auth/2fa-verify', {
                challenge_token: challengeToken,
                code,
                remember
            }, {
                skipGlobalErrorToast: true,
            });

            const data = response.data;

            if (data.success && data.token) {
                localStorage.setItem(ACCESS_TOKEN_KEY, data.token);

                if (data.refresh_token) {
                    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
                }

                if (data.user) {
                    localStorage.setItem(USER_INFO_KEY, JSON.stringify(data.user));
            syncSignedInHint();
                }

                return {
                    success: true,
                    data: {
                        accessToken: data.token,
                        refreshToken: data.refresh_token,
                        user: data.user
                    }
                };
            }

            return {
                success: false,
                message: mapAuthError(data.error) || t('Two-step verification failed')
            };
        } catch (error) {
            return {
                success: false,
                message: mapAuthError(error.response?.data?.error) || error.message || t('Two-step verification request failed')
            };
        }
    },

    // Google / GitHub 登录：已配置的提供方 { google: bool, github: bool }
    oauthProviders: async () => {
        try {
            const response = await apiClient.get('/auth/oauth/providers', { skipGlobalErrorToast: true });
            return response.data?.data || {};
        } catch (_) {
            return {};
        }
    },

    // 跳转到提供方（整页跳转，回调后带一次性 code 回到 /login#oauth_code=）
    oauthStartUrl: (provider, remember = true) =>
        `${apiClient.defaults.baseURL}/auth/oauth/${provider}/start?remember=${remember ? 1 : 0}&nonce=${encodeURIComponent(newOAuthNonce(OAUTH_LOGIN_NONCE_KEY))}`,

    // 一次性 code 换登录结果：与 login 相同（可能要求 2FA）
    oauthExchange: async (code) => {
        try {
            const nonce = takeOAuthNonce(OAUTH_LOGIN_NONCE_KEY);
            const response = await apiClient.post('/auth/oauth/exchange', { code, nonce }, { skipGlobalErrorToast: true });
            const data = response.data;
            if (data.success && data.requires_2fa) {
                return { success: true, requires2fa: true, challengeToken: data.challenge_token };
            }
            if (data.success && data.token) {
                localStorage.setItem(ACCESS_TOKEN_KEY, data.token);
                if (data.refresh_token) {
                    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
                }
                if (data.user) {
                    localStorage.setItem(USER_INFO_KEY, JSON.stringify(data.user));
                    syncSignedInHint();
                }
                return {
                    success: true,
                    data: { accessToken: data.token, refreshToken: data.refresh_token, user: data.user }
                };
            }
            return { success: false, message: mapAuthError(data.error) || t('Sign-in failed') };
        } catch (error) {
            return { success: false, message: mapAuthError(error.response?.data?.error) || error.message || t('Sign-in failed') };
        }
    },

    // 获取当前用户信息
    getCurrentUser: async () => {
        try {
            const response = await apiClient.get('/auth/check', {
                skipGlobalErrorToast: true, // 401 here is expected on bootstrap
            });
            const data = response.data;

            if (data.authenticated && data.user) {
                // 更新缓存的用户信息
                localStorage.setItem(USER_INFO_KEY, JSON.stringify(data.user));
            syncSignedInHint();

                // 如果响应中包含新令牌，更新它
                if (data.token) {
                    localStorage.setItem(ACCESS_TOKEN_KEY, data.token);
                }

                return {
                    success: true,
                    data: data.user
                };
            }

            return {
                success: false,
                status: response.status,
                message: t('Not signed in')
            };
        } catch (error) {
            if (error.response?.status === 401) {
                return {
                    success: false,
                    status: 401,
                    message: t('Token expired')
                };
            }

            return {
                success: false,
                message: error.message || t('Failed to load user info')
            };
        }
    },

    // 注册
    register: async (userData) => {
        try {
            const response = await apiClient.post('/auth/register', userData, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Registration failed')
            };
        }
    },

    // 重置密码请求
    // account = 用户名或邮箱；链接只发到账号注册邮箱
    resetPassword: async (account) => {
        try {
            const response = await apiClient.post('/auth/reset-request', { account }, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Password reset request failed')
            };
        }
    },

    // 验证邮箱（注册验证邮件里的 token）
    verifyEmail: async (token) => {
        try {
            const response = await apiClient.post('/auth/verify-email', { token }, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                expired: !!error.response?.data?.expired,
                message: error.response?.data?.error || error.message || t('Email verification failed')
            };
        }
    },

    // 重新发送验证邮件（防枚举：后端恒返回成功文案）
    resendVerification: async (email) => {
        try {
            const response = await apiClient.post('/auth/resend-verification', { email }, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Failed to send verification email')
            };
        }
    },

    // 确认重置密码
    confirmResetPassword: async (token, password) => {
        try {
            const response = await apiClient.post('/auth/reset-password', {
                token,
                password
            }, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Failed to confirm password reset')
            };
        }
    },

    // 刷新令牌
    refreshToken: async (refreshToken) => {
        try {
            const response = await apiClient.post('/auth/refresh-token', {
                refresh_token: refreshToken
            }, {
                skipGlobalErrorToast: true,
            });

            const data = response.data;

            if (data.success || data.token) {
                // 兼容旧的和新的响应格式
                return {
                    success: true,
                    data: {
                        accessToken: data.token,
                        refreshToken: data.refresh_token || refreshToken
                    }
                };
            }

            return {
                success: false,
                message: data.error || t('Failed to refresh token')
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Token refresh request failed')
            };
        }
    },

    // 登出
    logout: async () => {
        try {
            // 即使服务器请求失败，也会清除本地令牌
            await apiClient.post('/api/user/auth/logout').catch(() => {});

            // 清除所有令牌
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(USER_INFO_KEY);

            syncSignedInHint();
            return { success: true };
        } catch (error) {
            // 仍然清除本地令牌
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(USER_INFO_KEY);

            syncSignedInHint();
            return {
                success: false,
                message: error.message || t('Sign-out request failed')
            };
        }
    },

    // ==================== 两步验证管理（需登录） ====================

    // 查询 2FA 状态
    get2faStatus: async () => {
        try {
            const response = await apiClient.get('/api/user/2fa/status', {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Failed to load two-step verification status')
            };
        }
    },

    // 开始设置 2FA：返回密钥 + otpauth URI（供二维码），此时尚未启用
    setup2fa: async () => {
        try {
            const response = await apiClient.post('/api/user/2fa/setup', {}, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Failed to initialize two-step verification')
            };
        }
    },

    // 确认启用 2FA：验证码通过后返回一次性备用码（仅此一次）
    enable2fa: async (code) => {
        try {
            const response = await apiClient.post('/api/user/2fa/enable', { code }, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Failed to enable two-step verification')
            };
        }
    },

    // 停用 2FA：需账户密码 + 当前验证码（或备用码）
    disable2fa: async (password, code) => {
        try {
            const response = await apiClient.post('/api/user/2fa/disable', { password, code }, {
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || t('Failed to disable two-factor authentication')
            };
        }
    }
};

export default authService;
